import React from 'react';
import { User, Bell, Palette, Shield, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { Sidebar } from '../components/Sidebar';
import { TopBar } from '../components/TopBar';
import { Switch } from '../components/ui/switch';

export default function Settings() {
  const settingsSections = [
    {
      title: 'Profile',
      icon: User,
      items: [
        { label: 'Display Name', value: 'Alex Morgan', type: 'text' },
        { label: 'Email', value: 'alex@example.com', type: 'text' },
        { label: 'Avatar', value: 'Update profile picture', type: 'action' }
      ]
    },
    {
      title: 'Notifications',
      icon: Bell,
      items: [
        { label: 'Build Completion', value: true, type: 'toggle' },
        { label: 'Email Notifications', value: false, type: 'toggle' },
        { label: 'Build Failures', value: true, type: 'toggle' }
      ]
    },
    {
      title: 'Appearance',
      icon: Palette,
      items: [
        { label: 'Theme', value: 'Dark (Default)', type: 'text' },
        { label: 'Accent Color', value: 'Purple & Blue', type: 'text' }
      ]
    },
    {
      title: 'Performance',
      icon: Zap,
      items: [
        { label: 'Build Speed', value: 'Fast', type: 'text' },
        { label: 'Auto-optimize', value: true, type: 'toggle' }
      ]
    },
    {
      title: 'Security',
      icon: Shield,
      items: [
        { label: 'Two-Factor Auth', value: false, type: 'toggle' },
        { label: 'API Keys', value: 'Manage keys', type: 'action' }
      ]
    }
  ];

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <Sidebar />
      
      <div className="md:ml-64">
        <TopBar />
        
        <main className="p-6 md:p-12 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div>
              <h1
                className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-2"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="settings-title"
              >
                Settings
              </h1>
              <p className="text-lg text-muted-foreground">
                Customize your BuildAI experience
              </p>
            </div>

            <div className="space-y-6" data-testid="settings-sections">
              {settingsSections.map((section, sectionIndex) => {
                const Icon = section.icon;
                
                return (
                  <motion.div
                    key={section.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: sectionIndex * 0.1 }}
                    className="p-6 rounded-2xl border border-white/5 bg-white/5 space-y-6"
                    data-testid={`settings-section-${section.title.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {section.title}
                      </h2>
                    </div>

                    <div className="space-y-4">
                      {section.items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                          data-testid={`setting-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <span className="text-sm font-medium text-white">{item.label}</span>
                          
                          {item.type === 'toggle' ? (
                            <Switch defaultChecked={item.value} data-testid={`toggle-${item.label.toLowerCase().replace(/\s+/g, '-')}`} />
                          ) : item.type === 'action' ? (
                            <button
                              data-testid={`action-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              className="text-sm text-primary hover:text-primary/80 transition-colors"
                            >
                              {item.value}
                            </button>
                          ) : (
                            <span className="text-sm text-muted-foreground">{item.value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="pt-6 border-t border-white/5 flex justify-end gap-4"
            >
              <button
                data-testid="cancel-button"
                className="px-6 py-3 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                data-testid="save-button"
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-primary to-accent text-white font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/40 transition-all"
              >
                Save Changes
              </button>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
